import React from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grow,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField
} from "@mui/material";
import {visuallyHidden} from '@mui/utils';
import PropTypes from 'prop-types';
import {useNavigate} from "react-router-dom";
import ApplicationsData from "../../backend/dataservices/ApplicationsData";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AttendanceData from "../../backend/dataservices/AttendanceData";

function createData(employee, cost_center, course, year, status, boss, courseID, employeeID, attendanceID) {
    return {employee, cost_center, course, year, status, boss, courseID, employeeID, attendanceID};
}

function createSuggestionsData(_diszi, _name) {
    return {diszi: _diszi, name: _name};
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    {
        id: 'employee',
        numeric: false,
        disablePadding: false,
        label: 'Mitarbeiter',
    },
    {
        id: 'cost_center',
        numeric: false,
        disablePadding: false,
        label: 'Kostenstelle',
    },
    {
        id: 'course',
        numeric: false,
        disablePadding: false,
        label: 'Kurs',
    },
    {
        id: 'year',
        numeric: false,
        disablePadding: false,
        label: 'Jahr',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'boss',
        numeric: false,
        disablePadding: false,
        label: 'Diszi',
    },
    {
        id: 'delete',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

function EnhancedTableHead(props) {
    const {order, orderBy, onRequestSort} =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function ApplicationTable() {


    const [suggestions, setSuggestions] = React.useState([]);
    const [openSuggestion, setOpenSuggestion] = React.useState(false);
    const [suggestionValue, setSuggestionValue] = React.useState(null);
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('employee');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [loading, setLoading] = React.useState(false);
    const [rows, setRows] = React.useState([]);
    const [originalRows, setOriginalRows] = React.useState([]);
    const [inputValue, setInputValue] = React.useState("");
    const [showAttendanceDeleteDialog, setShowAttendanceDeleteDialog] = React.useState(false)
    const [attendanceDeleteDialogData, setAttendanceDeleteDialogData] = React.useState({})
    const [deleteAttendanceSnackbarOpen, setDeleteAttendanceSnackbarOpen] = React.useState(false);
    const [deleteAttendanceSnackbarText, setDeleteAttendanceSnackbarText] = React.useState("");
    const [deleteAttendanceSnackbarSeverity, setDeleteAttendanceSnackbarSeverity] = React.useState("success");
    const navigate = useNavigate();

    React.useEffect(() => {

        (async () => {
            await fetchApplicationsData();
            // eslint-disable-line react-hooks/exhaustive-deps
            await loadDisc();
            // eslint-disable-line react-hooks/exhaustive-deps
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    async function loadDisc() {
        setLoading(true);
        return ApplicationsData.fetchDisc().then(generateRows);
    }

    async function loadDataFromDatabase(rawDiscData) {
        let tempRows = [];
        for (const disc of rawDiscData) {
            tempRows.push(createSuggestionsData(disc, disc.D_Name + ' (' + disc.D_Diszi + ')'));
        }
        return tempRows;
    }

    function generateRows(rawDiscData) {
        loadDataFromDatabase(rawDiscData).then(data => {
            setSuggestions(data);
            setLoading(false);
        });
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function handleInputValue(value) {
        if (value != null) {
            const filteredRows = originalRows.filter((row) => {
                return row.boss.toLowerCase().includes(value.name.toLowerCase());
            });
            setRows(filteredRows);
        } else {
            setRows(originalRows);
        }
    }

    function fetchApplicationsData() {
        return ApplicationsData.fetchApplications()
            .then(atts => {
                let tempRows = [];
                if (!Array.isArray(atts)) { setOriginalRows([]); setRows([]); return; }
                atts.forEach(function (value, key) {
                    tempRows.push(createData(
                        value.M_Name + ', ' + value.M_Vorname,
                        value.M_Kostenstelle,
                        value.KS_Kursname,
                        new Date(value.KA_Datum).getFullYear(),
                        value.KA_Status.charAt(0).toUpperCase() + value.KA_Status.slice(1),
                        value.D_Name + ' (' + value.M_Diszi + ')',
                        value.KS_ID,
                        value['M_Pers-Nr'],
                        value.KA_ID
                    ));
                });
                setOriginalRows(tempRows);
                setRows(tempRows);
                return atts
            })
    }

    const toEmployees = (employee, employeeID) => {
        navigate("/employees", {state: {id: employeeID, name: employee}});
    }

    const toCourses = (courseName, courseID) => {
        navigate("/courses", {state: {id: courseID, name: courseName}});
    }

    function closeDeleteSnackbar() {
        setDeleteAttendanceSnackbarOpen(false);
    }

    function deleteCourse() {
        AttendanceData.deletePlannedAttendance(attendanceDeleteDialogData.attendanceID).then(value => {
            fetchApplicationsData();
            setDeleteAttendanceSnackbarSeverity("success");
            setDeleteAttendanceSnackbarText('Kurszuweisung erfolgreich gelöscht!')
            setDeleteAttendanceSnackbarOpen(true);
        }, (error) => {
            setDeleteAttendanceSnackbarSeverity("error");
            setDeleteAttendanceSnackbarText('Fehler beim Löschen der Kurszuweisung!');
            setDeleteAttendanceSnackbarOpen(true);
        }).then(value => {
            setShowAttendanceDeleteDialog(false);
            setAttendanceDeleteDialogData({});
        })
    }

    function handleAttendanceDialogClose() {
        setShowAttendanceDeleteDialog(false);
        setAttendanceDeleteDialogData({});
    }

    return (
        <Box sx={{width: '98%'}} style={{
            position: 'absolute', left: '50%',
            transform: 'translate(-50%)'
        }}>
            <Paper sx={{width: '100%', mb: 2}}>
                <Autocomplete options={suggestions}
                              sx={{marginLeft: '1%', marginTop: '10px', width: '30%'}}
                              open={openSuggestion}
                              onOpen={() => {
                                  setOpenSuggestion(true)
                              }}
                              onClose={() => {
                                  setOpenSuggestion(false)
                              }}
                              inputValue={inputValue}
                              onInputChange={(event, newInputValue) => {
                                  setInputValue(newInputValue)
                              }}

                              value={suggestionValue}
                              onChange={(event, newInputValue) => {
                                  setSuggestionValue(newInputValue);
                                  handleInputValue(newInputValue);
                              }}
                              isOptionEqualToValue={(option, value) => option.name.includes(value.name)}
                              getOptionLabel={(option) => option.name}
                              renderInput={(params => (
                                  <TextField
                                      {...params}
                                      variant={"outlined"}
                                      label={"Suche..."}
                                      sx={{width: '100%', marginTop: '30px'}}
                                      InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                              <React.Fragment>
                                                  {loading ? <CircularProgress color={"primary"}/> : null}
                                                  {params.InputProps.endAdornment}
                                              </React.Fragment>
                                          ),
                                      }}
                                  />
                              ))}/>

                <TableContainer>
                    <Table
                        sx={{minWidth: 750}}
                        width={'80%'}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 suggestions.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            tabIndex={-1}
                                            key={index}
                                        >
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                                style={{cursor: "pointer"}}
                                                onClick={() => toEmployees(row.employee, row.employeeID)}>
                                                {row.employee}
                                            </TableCell>
                                            <TableCell align="left">{row.cost_center}</TableCell>
                                            <TableCell
                                                align="left"
                                                style={{cursor: "pointer"}}
                                                onClick={() => toCourses(row.course, row.courseID)}>
                                                {row.course}
                                            </TableCell>
                                            <TableCell align="left">{row.year}</TableCell>
                                            <TableCell align="left">{row.status}</TableCell>
                                            <TableCell align="left">{row.boss}</TableCell>
                                            <TableCell align="left">
                                                <IconButton size={"large"} color={"error"} onClick={() => {
                                                    setAttendanceDeleteDialogData({
                                                        name: row.employee,
                                                        courseName: row.course,
                                                        courseID: row.courseID,
                                                        attendanceID: row.attendanceID
                                                    });
                                                    setShowAttendanceDeleteDialog(true);
                                                }}>
                                                    <DeleteForeverIcon/>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={showAttendanceDeleteDialog} onClose={handleAttendanceDialogClose}>
                <DialogTitle>
                    Geplanten Kurs löschen
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Möchten Sie den
                        Kurs {attendanceDeleteDialogData.courseName} für {attendanceDeleteDialogData.name} wirklich
                        löschen?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAttendanceDialogClose} variant={"text"}
                            style={{color: "gray"}}>Abbrechen</Button>
                    <Button color={"error"} variant={"text"} onClick={deleteCourse}>Löschen</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={deleteAttendanceSnackbarOpen} autoHideDuration={5000} onClose={closeDeleteSnackbar}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={closeDeleteSnackbar} severity={deleteAttendanceSnackbarSeverity} sx={{width: '100%'}}>
                    {deleteAttendanceSnackbarText}
                </Alert>
            </Snackbar>

        </Box>
    );
}

